import { useState, useEffect } from "react";
import { Upload, CheckCircle, AlertCircle, Package } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";

const ResidentialInventoryView = ({ clientId }) => {
  const t = useT();
  const [inventory, setInventory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}/residential/inventory`);
        setInventory(response.data);
      } catch (error) {
        console.error("Error fetching residential inventory:", error);
      }
    };
    fetchInventory();
  }, [clientId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMessage(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(`${API}/clients/${clientId}/residential/inventory/upload`, formData);
      setMessage({ type: "success", text: response.data.message });
      const invRes = await axios.get(`${API}/clients/${clientId}/residential/inventory`);
      setInventory(invRes.data);
    } catch (error) {
      setMessage({ type: "danger", text: error.response?.data?.detail || "Error uploading file" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="residential-inventory-view">
      <div className="page-header">
        <h2 className="page-title">{t("residential_inventory")}</h2>
        <p className="page-subtitle">{t("upload_csv_xlsx")}</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type} mb-lg`}>
          {message.type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="card mb-lg">
        <div className="file-upload-zone" onClick={() => document.getElementById("res-inv-file-input").click()}>
          <input id="res-inv-file-input" type="file" className="file-upload-input" accept=".csv,.xlsx,.xls" onChange={handleUpload} data-testid="res-inv-file-input" />
          <div className="file-upload-icon">{uploading ? <div className="spinner"></div> : <Upload size={48} strokeWidth={1.5} />}</div>
          <div className="file-upload-text">{uploading ? t("uploading") : "Drag & drop your file here or click to browse"}</div>
          <div className="file-upload-hint">Supported formats: CSV, Excel (.xlsx, .xls)</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Residential Inventory ({inventory.length})</h4>
        </div>
        {inventory.length === 0 ? (
          <div className="flex-center" style={{ padding: "var(--space-xxl)", color: "var(--text-muted)" }}>
            <div style={{ textAlign: "center" }}>
              <Package size={48} strokeWidth={1} />
              <p className="mt-md">No residential inventory items yet</p>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Brand</th>
                  <th>Model</th>
                  <th>Voltage (V)</th>
                  <th>Amperage (A)</th>
                  <th>Power (W)</th>
                  <th>Location</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.description}</td>
                    <td><span className="badge badge-neutral">{item.category}</span></td>
                    <td>{item.brand}</td>
                    <td>{item.model}</td>
                    <td>{item.voltage_v || "-"}</td>
                    <td>{item.amperage_a || "-"}</td>
                    <td>{item.power_w || "-"}</td>
                    <td>{item.location}</td>
                    <td><span className={`badge ${item.state === "active" ? "badge-success" : "badge-neutral"}`}>{item.state}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResidentialInventoryView;
